/**
 * Certification-line builder (frontend, ESM).
 *
 * Hand-mirrored from backend/new-ui-generation/shared/certification.js for the
 * same reason companyContract.js is duplicated: CRA (ESM) and the server (CJS)
 * cannot share a module. Change one, change the other.
 *
 * See the backend copy for the full rationale — in short, the
 * "CERTIFIED TRUE COPY … ON [DAY, DATE] AT [TIME]." sentence was a hardcoded
 * template in three renderers, shipping a literal placeholder for a date the
 * document already knew, and asserting "HELD AT THE REGISTERED OFFICE" even
 * when the venue row said otherwise.
 */

/** "2026-07-23" / "23 July 2026" → "Thursday, 23 July 2026". */
export const formatDayAndDate = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/^(mon|tue|wed|thu|fri|sat|sun)/i.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const weekday = d.toLocaleDateString('en-IN', { weekday: 'long' });
  const rest = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  return `${weekday}, ${rest}`;
};

/** "11:00 am" → "11:00 A.M." */
export const formatTime = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  const m = s.match(/^(\d{1,2})[:.](\d{2})\s*([ap])\.?\s*m\.?$/i);
  if (!m) return s;
  return `${Number(m[1])}:${m[2]} ${m[3].toUpperCase()}.M.`;
};

/** Is the meeting venue effectively the registered office? */
export const isRegisteredOffice = (venue, address) => {
  const v = String(venue || '').trim().toLowerCase();
  if (!v) return true;
  if (/registered\s+office/.test(v)) return true;
  const a = String(address || '').trim().toLowerCase();
  return Boolean(a) && (v === a || v.includes(a) || a.includes(v));
};

export function buildCertificationLine({
  companyName,
  address,
  venue,
  meetingDate,
  meetingTime,
  body = 'RESOLUTION PASSED AT THE MEETING OF THE BOARD OF DIRECTORS',
} = {}) {
  const name = String(companyName || '[COMPANY NAME]').trim();
  const addr = String(address || '').trim();
  const place = isRegisteredOffice(venue, addr)
    ? `HELD AT THE REGISTERED OFFICE OF THE COMPANY SITUATED AT ${addr || '[REGISTERED ADDRESS]'}`
    : `HELD AT ${String(venue).trim()}`;

  const when = formatDayAndDate(meetingDate) || '[DAY, DATE]';
  const at = formatTime(meetingTime) || '[TIME]';

  // formatTime already ends in a period ("11:00 A.M."), so a blind full stop
  // gave every dated document "AT 11:00 A.M.." on its certification line.
  const sentence = `CERTIFIED TRUE COPY OF THE ${body} OF ${name} ${place} ON ${when} AT ${at}`;
  return sentence.endsWith('.') ? sentence : `${sentence}.`;
}
